import axios from 'axios';
import type { AxiosError } from 'axios';
import AlreadyConnectedError from './AlreadyConnectedError';
import AlreadyCreatedError from './AlreadyCreatedError';
import ForbiddenError from './ForbiddenError';
import InvalidRequestError from './InvalidRequestError';
import type LivedigitalSDKError from './LivedigitalSDKError';
import NeedJoinFirstError from './NeedJoinFirstError';
import NotFoundError from './NotFoundError';
import RequestError from './RequestError';
import TimeoutError from './TimeoutError';
import TooManyRequestsError from './TooManyRequestsError';
import UnauthorizedError from './UnauthorizedError';

interface ErrorResponseBody {
  error?: string;
  errorCode?: string;
  [key: string]: unknown;
}

type ErrorFactory = (
  message: string,
  errorCode: string,
  meta?: Record<string, unknown>,
) => LivedigitalSDKError;

type ErrorConstructor = new (
  message?: string,
  errorCode?: string,
  meta?: Record<string, unknown>,
) => LivedigitalSDKError;

const createFactory = (Ctor: ErrorConstructor): ErrorFactory => (message, errorCode, meta) =>
  new Ctor(message, errorCode, meta);

const ERROR_FACTORIES: Record<string, ErrorFactory> = {
  check_access_fail: createFactory(ForbiddenError),
  join_room_fail: createFactory(InvalidRequestError),
  need_joined_to_channel: createFactory(NeedJoinFirstError),
  forbidden: createFactory(ForbiddenError),
  not_found: createFactory(NotFoundError),
  invalid_request: createFactory(InvalidRequestError),
  invalid_payload: createFactory(InvalidRequestError),
  already_created: createFactory(AlreadyCreatedError),
  too_many_requests: createFactory(TooManyRequestsError),
  something_went_wrong: createFactory(RequestError),
  di_error: createFactory(InvalidRequestError),
  resources_lock_error: createFactory(InvalidRequestError),
  already_connected: createFactory(AlreadyConnectedError),
  timeout: createFactory(TimeoutError),
  unauthorized: createFactory(UnauthorizedError),
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isErrorResponseBody = (value: unknown): value is ErrorResponseBody => {
  if (!isRecord(value)) {
    return false;
  }

  const { error, errorCode } = value;

  return (error === undefined || typeof error === 'string')
    && (errorCode === undefined || typeof errorCode === 'string');
};

const buildMeta = (
  error: AxiosError<ErrorResponseBody>,
  payload?: ErrorResponseBody,
): Record<string, unknown> | undefined => {
  const meta: Record<string, unknown> = {};

  if (payload && Object.keys(payload).length > 0) {
    meta.response = payload;
  }

  if (typeof error.response?.status === 'number') {
    meta.status = error.response.status;
  }

  if (error.response?.statusText) {
    meta.statusText = error.response.statusText;
  }

  if (error.code) {
    meta.axiosCode = error.code;
  }

  if (error.config?.url) {
    meta.url = error.config.url;
  }

  if (error.config?.method) {
    meta.method = error.config.method;
  }

  return Object.keys(meta).length > 0 ? meta : undefined;
};

export const mapSignalingApiError = (error: unknown): Error => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error : new RequestError();
  }

  const axiosError = error as AxiosError<ErrorResponseBody>;

  if (!axiosError.response) {
    const meta = buildMeta(axiosError);
    const message = axiosError.message || RequestError.DEFAULT_MESSAGE;
    const errorCode = axiosError.code ?? 'network_error';

    return new RequestError(message, errorCode, meta);
  }

  const payload = isErrorResponseBody(axiosError.response.data) ? axiosError.response.data : undefined;

  const errorCode = typeof payload?.errorCode === 'string' ? payload.errorCode : undefined;

  const messageFromPayload = typeof payload?.error === 'string' ? payload.error : undefined;
  const message =
    (messageFromPayload && messageFromPayload.trim().length > 0
      ? messageFromPayload
      : axiosError.message) || RequestError.DEFAULT_MESSAGE;

  const meta = buildMeta(axiosError, payload);

  if (errorCode) {
    const factory = ERROR_FACTORIES[errorCode];

    return factory ? factory(message, errorCode, meta) : new RequestError(message, errorCode, meta);
  }

  const fallbackCode =
    axiosError.code ??
    (typeof axiosError.response.status === 'number'
      ? `http_${ axiosError.response.status }`
      : 'something_went_wrong');

  return new RequestError(message, fallbackCode, meta);
};

export default mapSignalingApiError;
